// src/components/ui/PinBay.tsx — the 4-slot pin bay at the top of the Apps
// page (2026-11-04).
//
// Each filled slot is a launcher for a pinned app with two always-visible
// controls underneath: "Show on home" (a star; exactly ONE slot can hold it --
// that app's launcher also renders on the Home page under the banner) and
// "Unpin". Empty slots are dashed placeholders that explain how to fill them.
// Everything is tap-first: no hover states, 44px+ targets.

import type { CSSProperties } from "react";
import { C, F } from "../../theme";
import * as Icons from "./Icon";
import type { IconProps } from "./Icon";
import type { AppEntry } from "../../lib/apps";
import type { AppTileTone } from "./AppTile";

type IconComponent = (props: IconProps) => React.ReactElement;

function iconFor(key: string): IconComponent | null {
  const entry = (Icons as unknown as Record<string, unknown>)[key];
  return typeof entry === "function" ? (entry as IconComponent) : null;
}

const TONES: Record<AppTileTone, { bg: string; ic: string }> = {
  brass:   { bg: C.brassBg,   ic: C.brassDk   },
  heather: { bg: C.heatherBg, ic: C.heatherDk },
  sage:    { bg: C.sageBg,    ic: C.sageDk    },
  signal:  { bg: C.signalBg,  ic: C.signalDk  },
  paper:   { bg: C.paperWarm, ic: C.brassDk   },
};

export interface PinBayProps {
  slots: number;
  /** Resolved apps in slot order (null = empty slot). */
  apps: ReadonlyArray<AppEntry | null>;
  homeAppId: string | null;
  busy?: boolean;
  onOpen: (app: AppEntry) => void;
  onUnpin: (app: AppEntry) => void;
  onSetHome: (app: AppEntry | null) => void;
}

const controlBase: CSSProperties = {
  flex: 1,
  minHeight: 40,
  borderRadius: 10,
  border: `1px solid ${C.hairlineLt}`,
  background: C.card,
  color: C.inkSoft,
  fontFamily: F.body,
  fontWeight: 800,
  fontSize: 12,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 5,
  cursor: "pointer",
  padding: "0 6px",
  WebkitTapHighlightColor: "transparent",
  touchAction: "manipulation",
  whiteSpace: "nowrap",
};

export const PinBay = ({ slots, apps, homeAppId, busy = false, onOpen, onUnpin, onSetHome }: PinBayProps) => {
  const cells = Array.from({ length: slots }, (_, i) => apps[i] ?? null);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${slots}, minmax(0, 1fr))`,
        gap: 10,
        opacity: busy ? 0.7 : 1,
        transition: "opacity 120ms",
      }}
      aria-label="Pinned apps"
    >
      {cells.map((app, i) => {
        if (!app) {
          return (
            <div
              key={`empty-${i}`}
              style={{
                minHeight: 150,
                borderRadius: 18,
                border: `2px dashed ${C.hairline}`,
                background: "transparent",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: 10,
                textAlign: "center",
                color: C.inkMute,
                fontFamily: F.body,
                fontWeight: 700,
                fontSize: 12,
                lineHeight: 1.3,
              }}
            >
              <Icons.Pin s={20} c={C.inkMute} />
              <span>Empty slot</span>
              <span style={{ fontWeight: 600, fontSize: 11 }}>Tap the pin on any app</span>
            </div>
          );
        }
        const t = TONES[app.tone];
        const Icon = iconFor(app.iconKey);
        const isHome = homeAppId === app.id;
        return (
          <div
            key={app.id}
            style={{
              borderRadius: 18,
              border: `1px solid ${isHome ? C.brassLt : C.hairlineLt}`,
              background: C.card,
              boxShadow: isHome ? C.shadowM : C.shadowS,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 150,
            }}
          >
            <button
              type="button"
              onClick={() => onOpen(app)}
              aria-label={`Open ${app.name}`}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "12px 6px 8px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: C.ink,
                fontFamily: F.serif,
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation",
                minWidth: 0,
                width: "100%",
              }}
            >
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 13,
                  background: t.bg,
                  border: `1px solid ${C.hairlineLt}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {Icon && <Icon s={24} c={t.ic} />}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  textAlign: "center",
                  letterSpacing: -0.1,
                  overflowWrap: "anywhere",
                  width: "100%",
                }}
              >
                {app.name}
              </div>
            </button>
            <div style={{ display: "flex", gap: 4, padding: "0 6px 6px" }}>
              <button
                type="button"
                onClick={() => onSetHome(isHome ? null : app)}
                aria-pressed={isHome}
                aria-label={isHome ? `${app.name} is shown on Home; tap to remove` : `Show ${app.name} on Home`}
                title={isHome ? "Shown on Home — tap to remove" : "Show on Home"}
                style={{
                  ...controlBase,
                  border: `1px solid ${isHome ? C.brassLt : C.hairlineLt}`,
                  background: isHome ? C.brassBg : C.card,
                  color: isHome ? C.brassDk : C.inkSoft,
                }}
              >
                <Icons.Star s={15} c={isHome ? C.brassDk : C.inkMute} fill={isHome} />
                <span>{isHome ? "On Home" : "Home"}</span>
              </button>
              <button
                type="button"
                onClick={() => onUnpin(app)}
                aria-label={`Unpin ${app.name}`}
                title="Unpin"
                style={{ ...controlBase, flex: "0 0 40px", padding: 0 }}
              >
                <Icons.Close s={15} c={C.inkMute} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
